/**
 * Russian prepositional-case ("предложный падеж") region names.
 *
 * The RU region strings read "Тендеры и госзакупки в {{region}}", but the API
 * hands back the nominative name, so every region page shipped a grammatical
 * error: "в Андижанская область" instead of "в Андижанской области". These are
 * the head-term landing pages ("тендеры в Ташкенте"), so it is worth getting
 * right, and Russian declension can't be derived reliably from the nominative —
 * "Республика Каракалпакстан" and "город Ташкент" both decline differently from
 * the twelve "…ская область" names.
 *
 * Uzbekistan's administrative divisions are a fixed set of 14, so an explicit
 * table is simpler and safer than any runtime declension. Keyed by the region
 * CODE (the `/region/:slug` param), not the name, so it is independent of which
 * language the API renders names in.
 *
 * Uzbek needs no equivalent: its strings use a colon construction
 * ("{{region}}: tenderlar va davlat xaridlari").
 */
const RU_LOCATIVE: Record<string, string> = {
  AND: 'Андижанской области',
  BUK: 'Бухарской области',
  FER: 'Ферганской области',
  JIZ: 'Джизакской области',
  KAR: 'Республике Каракалпакстан',
  KAS: 'Кашкадарьинской области',
  KHO: 'Хорезмской области',
  NAM: 'Наманганской области',
  NAV: 'Навоийской области',
  SAM: 'Самаркандской области',
  SIR: 'Сырдарьинской области',
  SUR: 'Сурхандарьинской области',
  TAS: 'Ташкентской области',
  // "в Ташкенте" rather than "в городе Ташкенте" — it is what people search.
  TSH: 'Ташкенте',
};

/**
 * The RU name to interpolate after the preposition «в».
 *
 * Falls back to `fallback` (the API's nominative name) for an unknown code, which
 * is the pre-existing behaviour — a new region would read slightly wrong rather
 * than blank. `region-locative.test.ts` asserts all 14 live codes are covered.
 */
export function ruRegionLocative(code: string | undefined, fallback: string): string {
  if (!code) return fallback;
  return RU_LOCATIVE[code.toUpperCase()] ?? fallback;
}

/** Exported for the coverage test. */
export const RU_LOCATIVE_CODES = Object.keys(RU_LOCATIVE);
